import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { User, Target, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useClients } from '@/hooks/useClients'

interface ManualMeetingDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  existingMeeting?: {
    id: string
    client: string
    client_id: number
    lead_email: string
    email_domain: string
    campaign_name?: string
    meetings_booked_create_date: string
    source: string
    attribution_days: number
    direct_meeting: boolean
    lead_status?: string
    sf_account_id?: string
    meeting_tier?: string
  } | null
}

export const ManualMeetingDialog = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  existingMeeting 
}: ManualMeetingDialogProps) => {
  const { toast } = useToast()
  const { data: clients = [] } = useClients()
  
  const [formData, setFormData] = useState({
    client_id: existingMeeting?.client_id || '',
    lead_email: existingMeeting?.lead_email || '',
    email_domain: existingMeeting?.email_domain || '',
    campaign_name: existingMeeting?.campaign_name || '',
    meetings_booked_create_date: existingMeeting ? new Date(existingMeeting.meetings_booked_create_date) : new Date(),
    source: existingMeeting?.source || 'manual',
    attribution_days: existingMeeting?.attribution_days || 0,
    direct_meeting: existingMeeting?.direct_meeting ?? true,
    crm_account_link: existingMeeting?.sf_account_id || '',
    crm_opportunity: '',
    smartlead_email_thread: '',
    attribution_evidence: '',
    meeting_tier: existingMeeting?.meeting_tier || 'Tier 1'
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update form data when existingMeeting changes
  useEffect(() => {
    if (existingMeeting) {
      setFormData({
        client_id: existingMeeting.client_id || '',
        lead_email: existingMeeting.lead_email || '',
        email_domain: existingMeeting.email_domain || '',
        campaign_name: existingMeeting.campaign_name || '',
        meetings_booked_create_date: new Date(existingMeeting.meetings_booked_create_date),
        source: existingMeeting.source || 'manual',
        attribution_days: existingMeeting.attribution_days || 0,
        direct_meeting: existingMeeting.direct_meeting ?? true,
        crm_account_link: existingMeeting.sf_account_id || '',
        crm_opportunity: '',
        smartlead_email_thread: '',
        attribution_evidence: '',
        meeting_tier: existingMeeting.meeting_tier || 'Tier 1'
      })
    } else {
      // Reset form for new meeting
      setFormData({
        client_id: '',
        lead_email: '',
        email_domain: '',
        campaign_name: '',
        meetings_booked_create_date: new Date(),
        source: 'manual',
        attribution_days: 0,
        direct_meeting: true,
        crm_account_link: '',
        crm_opportunity: '',
        smartlead_email_thread: '',
        attribution_evidence: '',
        meeting_tier: 'Tier 1'
      })
    }
  }, [existingMeeting])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    if (!formData.client_id || !formData.lead_email || !formData.email_domain) {
      toast({
        title: "Validation Error",
        description: "Client, lead email, and email domain are required fields.",
        variant: "destructive"
      })
      return
    }

    setIsSubmitting(true)

    try {
      const selectedClient = clients.find(c => c.id === formData.client_id.toString())
      
      const meetingData = {
        client: selectedClient?.name || '',
        client_id: parseInt(formData.client_id.toString()),
        lead_email: formData.lead_email,
        email_domain: formData.email_domain,
        campaign_name: formData.campaign_name || null,
        meetings_booked_create_date: formData.meetings_booked_create_date.toISOString(),
        source: formData.source,
        attribution_days: formData.attribution_days,
        direct_meeting: formData.direct_meeting,
        lead_status: 'Meeting Booked',
        sf_account_id: formData.crm_account_link || null,
        meeting_tier: formData.meeting_tier,
        email_sent_time: null,
        op_created_date: null
      }

      let result
      if (existingMeeting) {
        // Update existing meeting
        result = await supabase
          .from('meetings_booked')
          .update(meetingData)
          .eq('id', existingMeeting.id)
      } else {
        // Create new meeting
        result = await supabase
          .from('meetings_booked')
          .insert([meetingData])
      }

      if (result.error) {
        throw result.error
      }

      toast({
        title: "Success",
        description: `Meeting ${existingMeeting ? 'updated' : 'created'} successfully.`
      })

      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error saving meeting:', error)
      toast({
        title: "Error",
        description: `Failed to ${existingMeeting ? 'update' : 'create'} meeting. Please try again.`,
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEmailChange = (email: string) => {
    setFormData(prev => {
      const domain = email.includes('@') ? email.split('@')[1] : ''
      return {
        ...prev,
        lead_email: email,
        email_domain: domain
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] z-[9999] bg-background border shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            {existingMeeting ? 'Edit Meeting' : 'Add Manual Meeting'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="w-4 h-4" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="client_id">Client *</Label>
                  <Select
                    value={formData.client_id.toString()}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, client_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lead_email">Lead Email *</Label>
                  <Input
                    id="lead_email"
                    type="email"
                    value={formData.lead_email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    placeholder="lead@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email_domain">Email Domain *</Label>
                  <Input
                    id="email_domain"
                    value={formData.email_domain}
                    onChange={(e) => setFormData(prev => ({ ...prev, email_domain: e.target.value }))}
                    placeholder="example.com"
                    required
                  />
                </div>
              </CardContent>
            </Card>

            {/* Meeting Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-4 h-4" />
                  Meeting Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="meetings_booked_create_date">Meeting Date *</Label>
                  <Input
                    id="meetings_booked_create_date"
                    type="date"
                    value={formData.meetings_booked_create_date.toISOString().split('T')[0]}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      meetings_booked_create_date: new Date(e.target.value) 
                    }))}
                    className="w-full"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, source: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[10000]">
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="breakcold">Breakcold</SelectItem>
                      <SelectItem value="sqls_meetings_booked">SQLS Attribution</SelectItem>
                      <SelectItem value="merged">Merged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="direct_meeting">Meeting Type</Label>
                    <Select
                      value={formData.direct_meeting.toString()}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, direct_meeting: value === 'true' }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        <SelectItem value="true">Direct</SelectItem>
                        <SelectItem value="false">Attribution</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meeting_tier">Meeting Tier</Label>
                    <Select
                      value={formData.meeting_tier}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, meeting_tier: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000]">
                        <SelectItem value="Tier 1">Tier 1</SelectItem>
                        <SelectItem value="Tier 2">Tier 2</SelectItem>
                        <SelectItem value="Tier 3">Tier 3</SelectItem>
                        <SelectItem value="Tier 4">Tier 4</SelectItem>
                        <SelectItem value="Individual">Individual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {!formData.direct_meeting && (
                  <div className="space-y-2">
                    <Label htmlFor="attribution_days">Attribution Days</Label>
                    <Input
                      id="attribution_days"
                      type="number"
                      value={formData.attribution_days}
                      onChange={(e) => setFormData(prev => ({ ...prev, attribution_days: parseInt(e.target.value) || 0 }))}
                      min="0"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Campaign Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Target className="w-4 h-4" />
                  Campaign Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign_name">Campaign Name</Label>
                  <Textarea
                    id="campaign_name"
                    value={formData.campaign_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, campaign_name: e.target.value }))}
                    placeholder="Campaign name"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smartlead_email_thread">Smartlead Email Thread</Label>
                  <Input
                    id="smartlead_email_thread"
                    value={formData.smartlead_email_thread}
                    onChange={(e) => setFormData(prev => ({ ...prev, smartlead_email_thread: e.target.value }))}
                    placeholder="Smartlead email thread link"
                  />
                </div>
              </CardContent>
            </Card>

            {/* External References */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ExternalLink className="w-4 h-4" />
                  External References
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="crm_account_link">Client CRM Account Link</Label>
                  <Input
                    id="crm_account_link"
                    value={formData.crm_account_link}
                    onChange={(e) => setFormData(prev => ({ ...prev, crm_account_link: e.target.value }))}
                    placeholder="CRM account link"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="crm_opportunity">Client CRM Opportunity</Label>
                  <Input
                    id="crm_opportunity"
                    value={formData.crm_opportunity}
                    onChange={(e) => setFormData(prev => ({ ...prev, crm_opportunity: e.target.value }))}
                    placeholder="CRM opportunity link"
                  />
                </div>

                {!formData.direct_meeting && (
                  <div className="space-y-2">
                    <Label htmlFor="attribution_evidence">Screenshot/Notes of Evidence</Label>
                    <Textarea
                      id="attribution_evidence"
                      value={formData.attribution_evidence}
                      onChange={(e) => setFormData(prev => ({ ...prev, attribution_evidence: e.target.value }))}
                      placeholder="Add screenshots or notes showing evidence of attribution"
                      rows={3}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (existingMeeting ? 'Update' : 'Create')} Meeting
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}